/* Pocket Factory · P2-REVIEW-1a1, "Review your order" (snippets/pf-review-sheet.liquid, assets/pf-review-sheet.css;
   docs/research/CHECKOUT-RESEARCH-1/REPORT.md §4 and §8 on task/CHECKOUT-RESEARCH-1). The cart drawer's Checkout and
   /cart's (their [data-pf-review-opener], with the button's own name="checkout") open this sheet instead of posting to
   checkout. The post is stopped at the click, before a submit exists, so the checkout count (pocket-analytics.js) and the
   first-order code's discount link (pocket-email-offer.js) see only the sheet's own Continue, a real checkout post.
   A Checkout that is held (aria-disabled, a plain button) or busy (disabled) is left to pocket.js, as before.
   P2-REVIEW-1a2: the product page's Buy (the bar's and the order box's) asks for it too, once the design is in the cart
   (pocket.js: pocket:review-order, cancelable); this takes the ask and opens, "Keep editing" for its second button.
   Buy now and the wallets never ask: they go straight to checkout.
   - The sheet opens at once with "Getting your order…", then fills from two requests made together:
     - the cart page's section (Section Rendering API: /cart?section_id=pocket-cart), whose lines it shows read-only;
     - /cart.js, for each line's properties, its location and the totals.
   - Each line keeps its words, but not its controls (− / +, Remove, Change, the design details):
     - its 3D snapshot (_pf_snapshot, _pf_snapshot_back, Worker asset addresses only), else the cart's own preview;
     - its pack and price;
     - EXP-3's ship-to, with the location numbered as the drawer numbers it and its estimated arrival (EXP-5's dates,
       window.PocketShipDates, with the location's state).
   - A line the Buy gate would stop (its data-design-gap, the rule PocketCore.designGap and /cart share) says the gate's
     own words with a link back to that design's page. Checkout is never held by it, as the cart never holds it
     (Oct 24 hotfix).
   - P2-REVIEW-1c: each design checks its link, as the Buy gate does (P2-LINKCHECK-1's part, window.PFLink): what a
     tap opens, the whole link byte for byte its Destination, Test link, from 1024px a QR, "Your cards ship locked to
     this link", a review card's place, and a review card on its website fallback said as such. A link the gate refuses
     (or none) shows the gate's own words in the part (not twice) and "Add the link your cards open": the Link step of
     that design, in place when the sheet was opened from its page, else its page. Not for a contact card.
   - Esc, ×, "Back to cart" and the browser's Back close it (one history entry while open) and give focus back to the
     Checkout that opened it. Tab stays inside.
   - Events, as the pfx_* events go to PostHog (pocket-cro.js's layer): review_opened, review_closed, review_continue,
     each with its source (drawer, cart, bar or box). Continue counts the checkout start (pocket-analytics.js), Buy's
     ('buy') when the bar or the box opened it.
   Node (module.exports) gets the pure part. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else { root.PFReviewSheet = api; api.start(root); }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  /** A line's snapshot picture: the Worker's own asset address, nothing else (as pocket-cart-preview.js takes a back). */
  const SNAPSHOT = /^https:\/\/[^\s"'<>]+\/v1\/assets\/asset_[a-f0-9]{32}\/content$/;
  const snapshotOf = value => SNAPSHOT.test(String(value || '').trim()) ? String(value).trim() : '';
  /** EXP-3's Expedited line ($10 a location): never a design of its own (it rides with its location). */
  const isShipLine = item => !!item?.properties?._pf_expedites || item?.handle === 'expedited-shipping';
  /** A design's location: its EXP-3 ship-to id, else the address entered at checkout. */
  const placeId = item => String(item?.properties?._ship_to_id || '').trim() || 'checkout';
  /** Words with their [placeholders] filled, as the drawer fills its data-ship-* words. */
  const said = (template, values) => Object.entries(values || {}).reduce((text, [k, v]) => text.split('[' + k + ']').join(String(v)), String(template || ''));

  /**
   * The order's locations, as the drawer numbers them (assets/pocket.js): the address entered at checkout first.
   * @param {Array} items - the cart's lines
   * @param {object} [multi] - the cart's _multi_ship attribute, parsed ({ addresses: { id: address }, expedited: [id] })
   * @returns {{ ids: string[], placed: boolean, number: (id: string) => number, label: (id: string) => string,
   *   state: (id: string) => string, fast: (id: string) => boolean }}
   */
  function locations(items, multi) {
    const designs = (items || []).filter(item => !isShipLine(item));
    const ids = [...new Set(designs.map(placeId))].sort((a, b) => a === 'checkout' ? -1 : b === 'checkout' ? 1 : 0);
    const addresses = multi && typeof multi === 'object' && multi.addresses && typeof multi.addresses === 'object' ? multi.addresses : {};
    const fast = new Set(Array.isArray(multi?.expedited) ? multi.expedited.map(String) : []);
    return {
      ids,
      placed: ids.length > 1 || (!!ids[0] && ids[0] !== 'checkout'),
      number: id => ids.indexOf(id) + 1,
      // Its words on a line, as the line's own "Ship to" says them.
      label: id => String(designs.find(item => placeId(item) === id)?.properties?.['Ship to'] || '').trim(),
      state: id => String(addresses[id]?.province || '').trim().toUpperCase(),
      // Expedited for that location (EXP-3 keeps the list in _multi_ship.expedited).
      fast: id => fast.has(String(id)),
    };
  }
  /** The cart's _multi_ship attribute, read leniently (EXP-3: { v: 1, addresses, lines, expedited }). */
  function multiShip(cart) {
    try {
      const raw = cart?.attributes?._multi_ship;
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return data && typeof data === 'object' ? data : null;
    } catch (_) { return null; }
  }
  /** The note before Continue: which address to enter at checkout (EXP-3's checkoutNote, the drawer's words). */
  function checkoutNote(plan, words) {
    if (!plan.placed) return '';
    if (plan.ids.length > 1 && plan.ids[0] === 'checkout') return words.notePrimary || '';
    return said(words.noteEnter, { address: plan.label(plan.ids[0]) });
  }
  /**
   * P2-REVIEW-1c: what one design's "Check the link" part is drawn from, the line's own properties (as the cart says them),
   * or null when it has none (an Expedited line, a contact card, a line without its card type).
   * @param {object} item - a /cart.js line (or {properties}, as the line's preview carries them)
   */
  function linkProps(item) {
    const p = item?.properties || {};
    const purpose = String(p['Card purpose'] || '').trim();
    if (isShipLine(item) || !purpose || purpose === 'Contact') return null;
    const text = key => String(p[key] ?? '');
    // A review card without its review link that Buy made a Website card on their website (assets/pocket.js toWebsiteCard,
    // owner's rule Oct 26; LINK-FALLBACK-1): said as what it is, a review card opening their website (its whole link is
    // the line's Destination), with "Add your Google review link".
    if (purpose === 'Website' && text('_Card type note') === 'review_card_without_link_made_website') {
      return { url: '', purpose: 'Reviews', placeName: '', website: text('Destination') || text('Website'), platform: text('Review platform').trim(), social: '', source: 'sheet' };
    }
    // The line's Destination as it is (what is printed); a review card's own review link when the line has no Destination.
    // A review card's website is never its link here: one in the cart without its review link is one (Buy made the others
    // Website cards), as /cart says.
    const url = text('Destination') || (purpose === 'Reviews' ? text('Review link') : '');
    return { url, purpose, placeName: text('Reviews go to').trim(), website: purpose === 'Reviews' ? '' : text('Website').trim(), platform: text('Review platform').trim(), social: text('Social destination').trim(), source: 'sheet' };
  }

  /** What a line still lacks, as the buy guide's gate words: designer, reviews, link or contact ('' when whole). */
  function gateKind(gap, purpose) {
    if (!gap) return '';
    if (gap === 'link') return purpose === 'Reviews' ? 'reviews' : 'link';
    if (gap === 'contact') return 'contact';
    return 'designer';
  }
  /**
   * The order in numbers, for the events: designs, locations, cards and the total in the shop's currency units.
   * @param {object} cart - /cart.js
   * @param {number} [cards] - the cards, as the cart page counts them (its lines' data-cards)
   */
  function summary(cart, cards) {
    const designs = (cart?.items || []).filter(item => !isShipLine(item));
    return {
      designs: designs.length,
      locations: locations(designs).ids.length,
      cards: Number.isFinite(cards) ? cards : designs.reduce((n, item) => n + (Number(item.quantity) || 0), 0),
      total: Math.round(Number(cart?.total_price) || 0) / 100,
    };
  }

  // ---------- In the browser ----------
  let started = false;
  function start(win) {
    const doc = win?.document;
    if (started || !doc) return;
    started = true;
    const go = () => { try { wire(win); } catch (_) {} };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', go, { once: true });
    else go();
  }

  function wire(win) {
    const doc = win.document, sheet = doc.querySelector('[data-pf-review-sheet]');
    if (!sheet || 'pfReviewReady' in sheet.dataset || typeof sheet.showModal !== 'function') return;
    sheet.dataset.pfReviewReady = '';
    const words = { ...sheet.dataset };
    const $ = selector => sheet.querySelector(selector);
    const title = $('#PfReviewTitle'), status = $('[data-pf-review-status]'), list = $('[data-pf-review-lines]');
    const note = $('[data-pf-review-note]'), promise = $('[data-pf-review-promise]'), totalNode = $('[data-pf-review-total]');
    // The Subtotal row shows once there is a total to say (not while it loads, nor when the order could not be shown).
    const totalRow = totalNode.closest('.pf-review__total') || totalNode, codeLine = $('[data-pf-review-code]');
    // BUILDLIST-2: "With 25 or more cards, this order ships with tracking." (only with the theme setting on).
    const track = $('[data-pf-review-track]');
    const continueButton = $('[data-pf-review-continue]'), continueWords = $('[data-pf-review-continue-words]');
    const backButton = $('[data-pf-review-close="back"]');
    const locale = doc.documentElement.lang || 'en-US';
    let opener = null, restore = null, source = '', how = '', request = 0, controller = null, numbers = null, pushed = false, ignorePop = false;
    // Opened from the product page (the bar's or the order box's Buy, P2-REVIEW-1a2) rather than the cart.
    const fromPage = () => source === 'bar' || source === 'box';

    const capture = (name, props) => {
      try { win.posthog?.capture?.(name, { flow: doc.documentElement.dataset.pfFlow || 'classic', layer: 'pfx-v1', ...props }); } catch (_) {}
    };
    // Its one live region stays in place (an empty one takes no room), so a change in it is always announced.
    const setStatus = text => { if (status.textContent !== text) status.textContent = text; };
    // A page shown again with its entry still marked (back from checkout, reloaded): the mark goes, so the next open
    // adds its own entry and Back stays on this page.
    try { if (win.history.state?.pfReview && !sheet.open) { const { pfReview, ...rest } = win.history.state; win.history.replaceState(rest, ''); } } catch (_) {}

    /* ---------- P2-REVIEW-1c: "Check the link" ---------- */
    // The part's script (window.PFLink), its QR's and its stylesheet, loaded once, on the sheet's first open: the sheet is
    // on every page, its link check only once it is open. A part drawn before they arrive is drawn by them (PFLink finds
    // the parts added to the page).
    const linkTemplate = sheet.querySelector('template[data-pf-review-link-part]');
    let linkLoading = null;
    function ensureLink() {
      if (win.PFLink?.render) return Promise.resolve(win.PFLink);
      if (linkLoading) return linkLoading;
      const d = sheet.dataset;
      if (!linkTemplate || !d.linkCheckSrc) return Promise.resolve(null);
      linkLoading = new Promise(resolve => {
        const done = () => resolve(win.PFLink?.render ? win.PFLink : null);
        if (d.linkCheckCss && ![...doc.querySelectorAll('link[rel="stylesheet"]')].some(l => l.getAttribute('href') === d.linkCheckCss)) {
          const css = doc.createElement('link');
          css.rel = 'stylesheet';
          css.href = d.linkCheckCss;
          doc.head.append(css);
        }
        const add = (src, data) => new Promise(loaded => {
          if (!src || [...doc.scripts].some(tag => tag.getAttribute('src') === src)) return loaded();
          const tag = doc.createElement('script');
          tag.src = src;
          tag.async = false;
          if (data) tag.dataset.qrSrc = data;
          tag.addEventListener('load', loaded, { once: true });
          tag.addEventListener('error', loaded, { once: true });
          doc.head.append(tag);
        });
        // The QR's script first (the part asks it for the code from 1024px), in order.
        Promise.all([add(d.qrSrc, d.qrGenerator), add(d.linkCheckSrc)]).then(done);
        win.setTimeout(done, 15000);
      });
      return linkLoading;
    }
    // "Add the link your cards open" (pf:link-fix from a part): that design's Link step. Opened from its own page (the
    // bar's or the order box's Buy), the sheet closes and the step opens there, focused (PFDesign.go); else its page.
    let thenGo = null;
    sheet.addEventListener('pf:link-fix', event => {
      const block = event.target?.closest?.('[data-pf-link-check]');
      if (!block || !sheet.contains(block)) return;
      const page = block.closest('[data-pf-review-here]') && fromPage() && win.PFDesign?.go ? win.PFDesign : null;
      const to = block.dataset.pfReviewEdit || '';
      if (!page && !to) return;
      how = 'link';
      thenGo = page ? () => { if (!page.go('customize', 'link') && to) win.location.assign(to); } : () => win.location.assign(to);
      sheet.close();
    });

    /* ---------- Opening ---------- */
    // The drawer's and /cart's Checkout: a live one (a submit button, neither held nor busy) opens the sheet.
    doc.addEventListener('click', event => {
      const button = event.target?.closest?.('button[name="checkout"]');
      const from = button?.closest('[data-pf-review-opener]');
      if (!from || sheet.contains(button) || event.defaultPrevented) return;
      if (button.disabled || button.type !== 'submit' || button.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      open(button, from.dataset.pfReviewOpener || 'cart');
    }, true);
    // P2-REVIEW-1a2: the bar's and the order box's Buy (assets/pocket.js) put the design in the cart, then ask for this
    // review (pocket:review-order, cancelable, { opener, source: 'bar' | 'box', restore }). Taking the ask tells Buy it
    // opened; an ask not taken (no sheet) leaves Buy to go on to checkout as before. Buy now and the wallets never ask.
    doc.addEventListener('pocket:review-order', event => {
      const detail = event.detail || {};
      // Taken only once it is open: a sheet that could not open leaves Buy to go on to checkout (never a dead end).
      try { open(detail.opener || null, detail.source === 'bar' ? 'bar' : 'box', detail.restore); } catch (_) { return; }
      if (sheet.open) event.preventDefault();
    });

    function open(button, from, back) {
      opener = button;
      restore = typeof back === 'function' ? back : null;
      source = from;
      how = '';
      numbers = null;
      // Its second button says where it goes back to: the cart, or the page the order was made on.
      if (backButton) backButton.textContent = (fromPage() ? words.backPage : words.backCart) || backButton.textContent;
      list.replaceChildren();
      note.hidden = true;
      promise.hidden = true;
      totalNode.textContent = '';
      totalRow.hidden = true;
      if (codeLine) { codeLine.textContent = ''; codeLine.hidden = true; }
      if (track) track.hidden = true;
      continueWords.textContent = said(words.continue, { total: '' }).replace(/\s*·\s*$/, '');
      delete sheet.dataset.pfReviewState;
      setStatus(words.loading || '');
      ensureLink();
      if (!sheet.open) sheet.showModal();
      title.focus({ preventScroll: true });
      // One history entry while it is open, so the browser's Back closes it. Opened again before the last close's Back
      // has landed, it is added when that Back lands (popstate, below).
      if (!ignorePop) remember();
      load();
    }
    function remember() {
      try {
        if (!win.history.state?.pfReview) { win.history.pushState({ ...(win.history.state || {}), pfReview: 1 }, ''); pushed = true; }
      } catch (_) { pushed = false; }
    }

    async function load() {
      const id = ++request;
      controller?.abort?.();
      controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      const signal = controller?.signal;
      try {
        const [html, cart] = await Promise.all([
          win.fetch(sheet.dataset.sectionUrl, { credentials: 'same-origin', signal, headers: { Accept: 'text/html' } }).then(r => r.ok ? r.text() : Promise.reject(new Error('section ' + r.status))),
          win.fetch(sheet.dataset.cartUrl, { credentials: 'same-origin', signal, headers: { Accept: 'application/json' } }).then(r => r.ok ? r.json() : Promise.reject(new Error('cart ' + r.status))),
        ]);
        if (id !== request || !sheet.open) return;
        const answered = win.performance?.now?.() ?? Date.now();
        draw(html, cart);
        // P2-REVIEW-1c: the lines drawn before the link check's script arrived get their part once it is here.
        if (list.querySelector('[data-pf-review-link]')) ensureLink().then(found => { if (found && id === request && sheet.open) for (const card of list.querySelectorAll('[data-pf-review-link]')) addLink(card); });
        // How long the sheet took to show the order once the cart answered (QA reads it).
        sheet.dataset.pfReviewMs = String(Math.round((win.performance?.now?.() ?? Date.now()) - answered));
        capture('review_opened', { source, ...numbers });
      } catch (error) {
        if (id !== request || !sheet.open || error?.name === 'AbortError') return;
        // Never a dead end: the order is still in the cart, and Continue still goes to checkout.
        sheet.dataset.pfReviewState = 'failed';
        setStatus(words.failed || '');
        capture('review_opened', { source, failed: true });
      }
    }

    /* ---------- The order, drawn from the cart page's own lines ---------- */
    function draw(html, cart) {
      const page = new win.DOMParser().parseFromString(html, 'text/html').querySelector('.cart-page');
      const articles = page ? [...page.querySelectorAll('.native-cart-lines > article.cart-line')] : [];
      const byKey = new Map((cart?.items || []).map(item => [item.key, item]));
      const plan = locations(cart?.items || [], multiShip(cart));
      if (!articles.length) {
        if (track) track.hidden = true;
        sheet.dataset.pfReviewState = 'empty';
        setStatus(words.empty || '');
        numbers = summary(cart, 0);
        return;
      }
      // The cards in all (for the events), and per location (BUILDLIST-2: the tracking line below).
      let cards = 0;
      const perPlace = new Map();
      const lines = articles.map(article => {
        const item = byKey.get(article.dataset.lineKey) || null;
        const ship = article.querySelector('[data-pf-ship-line]');
        const n = Number(ship?.dataset.cards) || 0;
        cards += n;
        perPlace.set(placeId(item), (perPlace.get(placeId(item)) || 0) + n);
        return lineFor(article, item, plan);
      });
      list.replaceChildren(...lines);
      const total = page.querySelector('.cart-total strong')?.textContent.trim() || '';
      totalNode.textContent = total;
      totalRow.hidden = !total;
      // LONGRUN-1102 (round 1: "every button still says $149" with the code held): what checkout charges with the 50%
      // code, under the subtotal, and on Continue. Display only: checkout applies it (PocketOffer.total, the Worker's rule).
      const coded = total ? withCode(cart) : '';
      if (codeLine) { codeLine.textContent = coded ? 'With your 50% code: ' + coded + ' at checkout.' : ''; codeLine.hidden = !coded; }
      continueWords.textContent = total ? said(words.continue, { total: coded || total }) : said(words.continue, { total: '' }).replace(/\s*·\s*$/, '');
      const noteText = checkoutNote(plan, words);
      note.textContent = noteText;
      note.hidden = !noteText;
      // The cart's own promise ("Works on arrival…"), the line that carries its free number.
      const kept = page.querySelector('.pf-cart-tel')?.closest('p');
      if (kept) { promise.textContent = kept.textContent.replace(/\s+/g, ' ').trim(); promise.hidden = false; }
      numbers = summary(cart, cards);
      // The cards of the cart it fetched (each line's pack × quantity, sections/pocket-cart.liquid's data-cards), counted
      // per location and the fewest taken, as the drawer counts them (assets/pocket.js renderCart): with Q2 (per order or
      // per address) open, the line shows only when every parcel has that many cards. Without the date script
      // (window.PocketShipDates), the line stays hidden.
      const fewest = perPlace.size ? Math.min(...perPlace.values()) : 0;
      if (track) track.hidden = !win.PocketShipDates?.tracks?.(fewest, track.dataset.pfShipFrom);
      sheet.dataset.pfReviewState = 'ready';
      setStatus('');
    }

    /** The cart's total with the 50% code this browser holds, in the shop's money ("$74.50"), or '' (no code, nothing
        it covers, or only checkout can say). */
    function withCode(cart) {
      try {
        const lines = (cart?.items || []).map(item => ({ title: item.variant_title || item.title || '', cents: Number(item.original_line_price ?? item.line_price) }));
        const total = lines.length ? win.PocketOffer?.total?.(lines) : null;
        if (!total?.off || !Number.isInteger(total.cents)) return '';
        return new Intl.NumberFormat(locale, { style: 'currency', currency: cart.currency || 'USD' }).format(total.cents / 100).replace(/\.00$/, '');
      } catch (_) { return ''; }
    }
    function lineFor(article, item, plan) {
      const props = item?.properties || readProps(article);
      const copy = doc.importNode(article, true);
      const editUrl = copy.querySelector('.cart-line-edit')?.getAttribute('href') || copy.querySelector('.pf-cart-finish')?.getAttribute('href') || item?.url || '';
      const count = copy.querySelector('.cart-line-count')?.textContent.replace(/\s+/g, ' ').trim() || '';
      const price = copy.querySelector('[data-line-price]');
      // An Expedited location's shipping, as /cart's options show it once chosen (kept before the options go).
      const fastFace = copy.querySelector('[data-pf-ship-face="expedited"]');
      // Read-only: its controls and its long details go; its words stay.
      copy.querySelectorAll('details,.cart-line-controls,.cart-line-packs,.cart-line-edit,button,input,select,textarea,script,style,noscript,[data-pf-ship-options]').forEach(node => node.remove());
      copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      copy.querySelectorAll('h2').forEach(h2 => { const h3 = doc.createElement('h3'); h3.className = h2.className; h3.textContent = h2.textContent; h2.replaceWith(h3); });
      copy.classList.add('pf-review__card');
      copy.removeAttribute('data-line-id');
      const name = copy.querySelector('h3')?.textContent.trim() || '';
      // Its picture: the 3D snapshot the line carries, else the cart's own preview of the line.
      let media = copy.querySelector('.pf-cart-preview');
      const front = snapshotOf(props?._pf_snapshot), back = snapshotOf(props?._pf_snapshot_back);
      if (front && media) {
        const figure = doc.createElement('div');
        figure.className = 'pf-review__media';
        figure.dataset.pfReviewMedia = 'snapshot';
        // A snapshot is the card face itself (pocket-design-snapshot.js), so its shape is the card's (CR80: 85.6 × 54).
        const tall = props?.Orientation === 'Vertical';
        for (const [url, alt] of [[front, words.front], [back, words.back]]) {
          if (!url) continue;
          const img = doc.createElement('img');
          img.src = url;
          img.alt = said(alt, { name });
          img.loading = 'lazy';
          img.decoding = 'async';
          img.width = tall ? 404 : 640;
          img.height = tall ? 640 : 404;
          figure.append(img);
        }
        media.replaceWith(figure);
        media = figure;
      } else if (media) {
        media.dataset.pfReviewMedia = 'preview';
        try { win.PocketCartPreview?.render?.(media, props || {}); } catch (_) {}
      }
      // Its pack and price, on one row.
      if (count || price) {
        const row = doc.createElement('p');
        row.className = 'pf-review__pack';
        const n = doc.createElement('span');
        n.textContent = count;
        row.append(n);
        if (price) { const strong = doc.createElement('strong'); strong.textContent = price.textContent.trim(); row.append(' ', strong); price.remove(); }
        const place = copy.querySelector('[data-pf-ship-line]');
        if (place) place.before(row); else copy.append(row);
      }
      // Where it goes and when it arrives (EXP-3 and EXP-5).
      const ship = copy.querySelector('[data-pf-ship-line]');
      if (ship && item) {
        const id = placeId(item);
        if (plan.placed) {
          const where = ship.querySelector('.pf-ship-line__to');
          if (where) {
            where.textContent = said(words.location, { n: plan.number(id) }) + ' · ' + (id === 'checkout' ? words.shipsCheckout : said(words.shipsTo, { label: plan.label(id) }));
          }
        }
        const plain = ship.querySelector('[data-pf-ship-plain]');
        if (plain && fastFace && plan.fast(id)) plain.replaceChildren(...fastFace.childNodes);
        for (const node of ship.querySelectorAll('[data-pf-ship-arrival]')) node.textContent = arrival(node.dataset.pfShipArrival === 'expedited' ? 'expedited' : 'standard', plan.state(id));
      }
      // P2-REVIEW-1c: what a tap on this design opens, checked as the Buy gate checks it (addLink, below). Before the link
      // check's script is here, the line is marked and keeps the gate's words; it gets its part when the script arrives.
      const linked = linkProps(item || { properties: props });
      if (linked) copy.dataset.pfReviewLink = JSON.stringify({ ...linked, edit: editUrl, key: String(props?.['_Design key'] || '') });
      // What the Buy gate would still ask for, in its own words, with the way back to that design. A link it would stop
      // is said by the link check above (its words and its fix), never twice.
      const kind = gateKind(copy.dataset.designGap || '', props?.['Card purpose']);
      const template = kind ? sheet.querySelector(`template[data-pf-review-gate="${kind}"]`) : null;
      if (template) {
        const gate = template.content.firstElementChild.cloneNode(true);
        const link = gate.querySelector('a');
        if (link && editUrl) link.href = editUrl; else link?.remove();
        const after = copy.querySelector('.cart-line-note') || copy.querySelector('.cart-line-opens') || copy.querySelector('.cart-line-variant') || copy.querySelector('h3');
        if (after) after.after(gate); else copy.append(gate);
        copy.dataset.pfReviewGate = kind;
      }
      // Its words in one column beside its picture (pf-review-sheet.css), in their order.
      const column = doc.createElement('div');
      column.className = 'pf-review__words';
      column.append(...[...copy.children].filter(node => node !== media));
      copy.append(column);
      const li = doc.createElement('li');
      li.className = 'pf-review__line';
      li.append(copy);
      if (win.PFLink?.render && copy.dataset.pfReviewLink) addLink(copy);
      return li;
    }
    /**
     * P2-REVIEW-1c: one design's "Check the link" (P2-LINKCHECK-1's part, from the sheet's template), drawn from the line's
     * own properties, under its words in place of its short "A tap opens …" / "Reviews go to: …". A link the gate would stop
     * is said by the part (the gate's own words and its fix), so the gate's line for it goes: never said twice.
     */
    function addLink(card) {
      let linked = null;
      try { linked = JSON.parse(card.dataset.pfReviewLink || 'null'); } catch (_) {}
      delete card.dataset.pfReviewLink;
      const block = linked && win.PFLink?.render ? linkTemplate?.content.querySelector('[data-pf-link-check]')?.cloneNode(true) : null;
      if (!block) return;
      for (const [key, attr] of [['url', 'url'], ['purpose', 'purpose'], ['placeName', 'place'], ['website', 'website'], ['platform', 'platform'], ['social', 'social'], ['source', 'source']]) block.dataset[attr] = String(linked[key] ?? '');
      block.dataset.pfReviewEdit = String(linked.edit || '');
      block.classList.add('pf-review__link');
      try { win.PFLink.render(block); } catch (_) { return; }
      const scope = card.querySelector('.pf-review__words') || card;
      const opens = scope.querySelector('.cart-line-opens');
      const at = opens || scope.querySelector('.pf-review__gate') || scope.querySelector('.cart-line-note') || scope.querySelector('.cart-line-variant') || scope.querySelector('h3');
      if (at) at.after(block); else scope.append(block);
      opens?.remove();
      if (!['ok', 'website'].includes(block.dataset.state || '') && ['link', 'reviews'].includes(card.dataset.pfReviewGate || '')) {
        scope.querySelector('.pf-review__gate')?.remove();
        delete card.dataset.pfReviewGate;
      }
      // The design this page is editing (its Link step is here): its _Design key is the page's.
      const here = doc.querySelector('[data-pocket-product] form.product-form [name="properties[_Design key]"]')?.value;
      if (!here || linked.key !== here) return;
      block.dataset.pfReviewHere = '';
      // One of the things to look at, never a stop: art from their words still being made (it is not on this order).
      if (words.artMaking && win.PocketDesign?.page?.words?.().art?.state === 'making') {
        const look = doc.createElement('p');
        look.className = 'pf-review__look';
        look.dataset.pfReviewLook = 'art';
        look.textContent = words.artMaking;
        block.after(look);
      }
    }
    // A line's properties as its preview carries them (data-cart-properties), when /cart.js has no such line.
    function readProps(article) {
      try { return JSON.parse(article.querySelector('[data-cart-properties]')?.dataset.cartProperties || '{}'); } catch (_) { return {}; }
    }
    // A shipping option's estimated arrival for a location's state (assets/pf-exp-shipdate.js), in EXP-3's words.
    function arrival(kind, state) {
      const d = win.PocketShipDates;
      if (!d?.estimate || !d.format || !words.arrival) return '';
      try {
        const e = d.estimate(kind, { state });
        return said(words.arrival, { from: d.format(e.arrival.from, locale), to: d.format(e.arrival.to, locale) });
      } catch (_) { return ''; }
    }

    /* ---------- Continuing, and closing ---------- */
    continueButton.addEventListener('click', () => {
      how = 'continue';
      capture('review_continue', { source, ...(numbers || {}) });
      // The checkout starts here: Buy's, as pocket-analytics.js counted it when Buy opened checkout itself, or the cart's.
      // Counted once: the cart form's own submit, a moment later, is the same start (pocket-analytics.js).
      try { win.PocketAnalytics?.checkoutStarted?.(fromPage() ? 'buy' : 'cart'); } catch (_) {}
    });
    sheet.addEventListener('click', event => {
      const close = event.target?.closest?.('[data-pf-review-close]');
      if (close && sheet.contains(close)) { how = close.dataset.pfReviewClose || 'close'; sheet.close(); return; }
      // A click on the backdrop (outside the dialog, from 1024px) closes it, as it closes the cart drawer.
      if (event.target !== sheet) return;
      const r = sheet.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) { how = 'backdrop'; sheet.close(); }
    });
    sheet.addEventListener('cancel', () => { how = 'escape'; });
    // Back from checkout to a page the browser kept: the sheet shows the cart as it is now.
    win.addEventListener('pageshow', event => { if (event.persisted && sheet.open) { how = ''; load(); } });
    win.addEventListener('popstate', event => {
      if (ignorePop) { ignorePop = false; if (sheet.open) remember(); return; }
      if (!sheet.open || event.state?.pfReview) return;
      pushed = false;
      how = 'history';
      sheet.close();
    });
    sheet.addEventListener('close', () => {
      request++;
      controller?.abort?.();
      if (how === 'continue') return;
      capture('review_closed', { source, how: how || 'close', ...(numbers || {}) });
      // Its history entry goes with it, unless the browser's Back already took it.
      if (pushed && win.history.state?.pfReview) { ignorePop = true; pushed = false; try { win.history.back(); } catch (_) { ignorePop = false; } }
      pushed = false;
      const back = opener, again = restore;
      opener = null;
      restore = null;
      // P2-REVIEW-1c: "Add the link your cards open" goes on to that design's Link step (focus goes there).
      if (thenGo) { const go = thenGo; thenGo = null; try { go(); } catch (_) {} return; }
      if (back?.isConnected) back.focus({ preventScroll: true });
      // The Buy pressed may have stepped aside meanwhile (the bar's buttons change with the cart): the one beside it.
      if ((!back || doc.activeElement !== back) && again) { try { again(); } catch (_) {} }
    });
    // Tab and Shift+Tab stay inside the sheet: they wrap at its ends (and from its title), and between them the browser
    // moves focus as usual, the order's scrolling area included where the browser lets a keyboard scroll it.
    sheet.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const focusable = [...sheet.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])')].filter(node => node.getClientRects().length && !node.closest('[hidden],template'));
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1], at = doc.activeElement;
      const to = !sheet.contains(at) ? first : event.shiftKey ? (at === first || at === title || at === sheet ? last : null) : at === last ? first : null;
      if (to) { event.preventDefault(); to.focus(); }
    });
  }

  return { SNAPSHOT, snapshotOf, isShipLine, placeId, said, locations, multiShip, checkoutNote, gateKind, linkProps, summary, start };
});
